import React, { RefObject } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { ICompany, IProjectStore } from 'store/companies/companiesTypes';
import { IUsers, RootState } from 'store/store';
import BubbleLoading from 'components/BubbleLoading';
import { display } from 'components/ErrorView/ErrorDialog';
import MessageSender from './MessageSender';
import MessageView from './MessageView';
import {
  ChatContainer,
  ChatInputLoading,
  ChatNotAllowedBox,
  LoadingParent,
  MsgList,
} from './styles';
import { appDispatch } from 'store/store';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  IFileMessageContent,
  IMessageState,
  IMessageViewServer,
  ISimpleMessageContent,
  sendMessage,
  sendMessageFn,
} from 'store/messages/messagesSlice';
import {
  IConversation,
  IConversationState,
  loadOlderMessages,
  loadOlderMessagesFn,
  requireEndOfConversation,
  requireEndOfConversationFn,
} from 'store/conversation/conversationSlice';
import i18n from '../../../i18n';

interface IStateToProps {
  users: IUsers['map'];
  thisUser: IThisUserState;
  systemId: number;
  inboxMessages: IMessageState['data'];
  conversations: IConversationState['data'];
  viewAllProjectMemberIds: number[];
}

interface IDispatchToProps {
  sendMessage: (
    message: IMessageViewServer,
    parseContent: boolean,
    file: any,
    userId: number,
    conversationToken?: string,
    onProgressCallback?: any
  ) => ReturnType<typeof sendMessageFn>;
  loadOlderMessages: (
    conversationId: number,
    conversationToken?: string
  ) => ReturnType<typeof loadOlderMessagesFn>;
  requireEndOfConversation: (
    conversationId: number,
    conversationToken?: string
  ) => ReturnType<typeof requireEndOfConversationFn>;
}

interface IOwnProps {
  readToken?: string;
  sendToken?: string;
  conversationId: number;
  projectId?: number;
  messageSent?: (msg: ISimpleMessageContent | IFileMessageContent) => void;
}

interface IComponentStates {
  error: boolean;
  loading: boolean;
  canSendMessage: boolean;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

class TaskConversation extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    error: false,
    loading: true,
    canSendMessage: true,
  };

  conversation: RefObject<HTMLDivElement> = React.createRef();
  scrollDown = 0;

  componentDidMount() {
    const { thisUser, readToken, sendToken, conversationId } = this.props;

    this.props
      .requireEndOfConversation(conversationId, readToken)
      .then(({ conversation: chat }: { conversation: IConversation }) => {
        if (readToken) {
          const userId = thisUser.userId;
          const canSendMessage =
            chat.participantIds.includes(Number(userId)) || !!sendToken;
          this.setState({ loading: false, canSendMessage });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((_: any) => {
        this.setState({ loading: false, error: true });
        display(i18n.t('unknownError', { ns: 'general' }));
      });

    const conversation = this.conversation.current;
    if (conversation) {
      conversation.scrollTop =
        conversation.scrollHeight - conversation.clientHeight;
      conversation.addEventListener('scroll', this.handleOnScroll);
    }
  }

  UNSAFE_componentWillReceiveProps() {
    const conversation = this.conversation.current;
    if (conversation) {
      const scrollTop = conversation.scrollTop;
      const scrollHeight = conversation.scrollHeight;
      this.scrollDown = scrollHeight - scrollTop;
    }
  }

  componentDidUpdate(prevProps: Readonly<IComponentProps>) {
    const conversation = this.conversation.current;
    if (this.props.conversationId !== prevProps.conversationId)
      this.componentDidMount();
    else {
      if (conversation) {
        conversation.scrollTop = conversation.scrollHeight - this.scrollDown;
      }
    }
  }

  componentWillUnmount() {
    const conversation = this.conversation.current;
    if (conversation) {
      conversation.removeEventListener('scroll', this.handleOnScroll);
    }
  }

  handleOnScroll = () => {
    const conversation = this.conversation.current;
    let scrollTop = 0;
    if (conversation) {
      scrollTop = conversation.scrollTop;
    }
    const scrolledToTop = scrollTop < 200;
    const loadingMoreMessages =
      this.props.conversations[this.props.conversationId].loadingOlderMessages;
    const loadAll =
      this.props.conversations[this.props.conversationId].loadAllOlderMessages;
    if (
      scrolledToTop &&
      typeof loadingMoreMessages === 'undefined' &&
      !this.state.error &&
      !loadAll
    ) {
      this.loadMoreMessages();
    }
  };

  handleSendMessage = async (
    msgContent: ISimpleMessageContent | IFileMessageContent,
    parseContent: boolean,
    file: Blob
  ) => {
    const messageView: IMessageViewServer = {
      content: JSON.stringify(msgContent),
      sendStatus: 'sent',
      conversationId: this.props.conversationId,
      senderId: Number(this.props.thisUser.userId),
    };
    await this.props.sendMessage(
      messageView,
      parseContent,
      file,
      Number(this.props.thisUser.userId),
      this.props.sendToken || undefined
    );
    this.props.messageSent?.(msgContent);
  };

  loadMoreMessages = () => {
    this.props
      .loadOlderMessages(this.props.conversationId, this.props.readToken)
      .catch((_: any) => {
        this.setState({ error: true });
        display(i18n.t('unknownError', { ns: 'general' }));
      });
  };

  render() {
    const {
      users,
      systemId,
      thisUser,
      readToken,
      inboxMessages,
      conversations,
      conversationId,
      viewAllProjectMemberIds,
    } = this.props;

    const { loading, canSendMessage } = this.state;

    const loadingOlderMessages =
      conversations[conversationId]?.loadingOlderMessages;
    return (
      <ChatContainer className="task-conversation-container">
        <MsgList ref={this.conversation}>
          {(this.state.loading || loadingOlderMessages) && (
            <LoadingParent>
              <BubbleLoading />
            </LoadingParent>
          )}
          {inboxMessages
            ? Object.values(inboxMessages)
                .filter((message) => message.conversationId === conversationId)
                .sort((a, b) => a.sendTime - b.sendTime)
                .map((message) => {
                  let whoIsSender: 'others' | 'myself' | 'system' = 'others';
                  if (systemId === message.senderId) {
                    whoIsSender = 'system';
                  } else if (message.senderId === Number(thisUser.userId)) {
                    whoIsSender = 'myself';
                  }
                  return (
                    <MessageView
                      key={message.id}
                      content={message.content}
                      messageTime={message.sendTime}
                      whoIsSender={whoIsSender}
                      thisUser={thisUser}
                      senderName={users[message.senderId]?.name || ''}
                      uploading={message.uploading!}
                    />
                  );
                })
            : null}
        </MsgList>
        {readToken && loading ? (
          <ChatInputLoading />
        ) : canSendMessage ? (
          <MessageSender
            sendMessage={this.handleSendMessage}
            users={users}
            conversation={conversations[conversationId]}
            possibleMembers={viewAllProjectMemberIds}
          />
        ) : (
          <ChatNotAllowedBox>
            {i18n.t('canNotSendMessage', { ns: 'taskManager' }) as string}
          </ChatNotAllowedBox>
        )}
      </ChatContainer>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const users = state.users.map;
  const systemId = state.conversations.systemId;
  const thisUser = state.thisUser;
  const conversations = state.conversations.data;
  const inboxMessages = state.messages.data;
  let project: IProjectStore | undefined = undefined;
  let company: ICompany | undefined = undefined;
  Object.values(state.companies.data || {}).forEach((cmp) => {
    Object.values(cmp.projects || {}).forEach((prj) => {
      if (prj.id === props.projectId) {
        project = prj;
        company = cmp;
      }
    });
  });

  const viewAllProjectMemberIds: number[] = [];
  Object.values(
    (project as unknown as IProjectStore)?.projectMembers || {}
  ).forEach((member) => {
    if (project!.visibleAllTasks || member.visibleAllTasks) {
      viewAllProjectMemberIds.push(member.userId!);
    }
  });
  Object.values(
    (project as unknown as IProjectStore)?.projectRoleMembers || {}
  ).forEach((member) => {
    if (project!.visibleAllTasks || member.visibleAllTasks) {
      company!.roles[member.roleId!]?.users.forEach((it) =>
        viewAllProjectMemberIds.push(it.userId)
      );
    }
  });

  return {
    users,
    systemId,
    thisUser,
    conversations,
    inboxMessages,
    viewAllProjectMemberIds,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    requireEndOfConversation: (
      conversationId: number,
      conversationToken?: string
    ) =>
      appDispatch(
        requireEndOfConversation({ conversationId, conversationToken })
      ),
    sendMessage: (
      message: IMessageViewServer,
      parseContent: boolean,
      file: any,
      userId: number,
      conversationToken?: string,
      onProgressCallback?: any
    ) => {
      return appDispatch(
        sendMessage({
          msg: message,
          parseContent,
          file,
          userId,
          conversationToken,
          onProgressCallback,
        })
      );
    },
    loadOlderMessages: (conversationId: number, conversationToken?: string) =>
      appDispatch(loadOlderMessages({ conversationId, conversationToken })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskConversation);
